import {
  AfterViewInit,
  Component,
  Inject,
  Input,
  OnInit,
  forwardRef
} from '@angular/core';
import { UIRouter }                from '@uirouter/core';
import { TranslateService }       from '@ngx-translate/core';

import { ApiService }        from '../api/api.service';
import { ConfirmService }    from '../messages/confirm/confirm.service';
import { MessagesService }   from '../messages/messages.service';
import { MessengerService }  from '../messages/messenger/messenger.service';
import { TableService }      from '../table/table.service';
import { EditComponent }     from '../table/cell-components/actions/edit.component';

@Component({
  selector   : 'wor-customers-list',
  template: require('./customers.list.component.html')
})
export class CustomersListComponent implements AfterViewInit, OnInit {
  @Input() customers: any;
  @Input() companyMessages: any;
  @Input() actionsFactory: any;

  filtering: any;
  tableSettings: any;

  private tableCacheKey = 'customers';

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService       : ApiService,
    @Inject(forwardRef(() => ConfirmService)) private confirmService   : ConfirmService,
    @Inject(forwardRef(() => MessagesService)) private messagesService  : MessagesService,
    @Inject(forwardRef(() => MessengerService)) private messengerService : MessengerService,
    private router           : UIRouter,
    @Inject(forwardRef(() => TableService)) private tableService     : TableService,
    private translateService : TranslateService
  ) {}

  ngOnInit () {
    const actionsFactory = this.actionsFactory || EditComponent;

    this.filtering = this.tableService.setFiltersFromCache(this.tableCacheKey, {
      archived_only: false,
      value        : ''
    });

    const columns: any[] = [
      {
        field : 'display_name',
        header: { text: this.translateService.instant('CUSTOMER_SPACE.CUSTOMER') }
      },
      {
        field : 'mobile',
        header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.MOBILE') },
        pipe  : 'mobilePhone'
      },
      {
        field : 'phone',
        header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.PHONE_NUMBER') },
        pipe  : 'phone'
      },
      {
        component: {
          bindings: {
            outputs: {
              onEditClick      : event => this.viewCustomer(event),
              onAddVehicleClick: event => this.router.stateService.go('app.vehicle', { customer_id: event.id }),
              onBookingClick   : event => this.router.stateService.go('app.event', { customer_id: event.id }),
              onInvoiceClick   : event => this.router.stateService.go('app.invoice', { customer_id: event.id }),
              onSMSClick       : event => this.sendSMS(event),
              onEmailClick     : event => this.sendEmail(event),
              onActivateClick  : event => this.activate(event)
            }
          },
          enabled: true,
          factory: actionsFactory
        },
        sortable: false
      }
    ];

    this.tableSettings = {
      broadcast: 'filterCustomers',
      cache    : {
        enabled: true,
        key    : this.tableCacheKey
      },
      columns,
      empty: {
        text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_CUSTOMERS_FOUND')
      },
      pagination: {
        enabled   : true,
        pageSize  : this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 10,
        pageSizes : [ 10, 25, 50 ],
        queryCount: () => this.apiService.get('/filters/count', [
          this.filtering.value || '*',
          'customers',
          this.filtering.archived_only
        ])
      },
      rows: {
        highlight: { enabled: true }
      },
      sorting: this.tableService.setSortingFromCache(this.tableCacheKey, {
        direction: 'asc',
        field    : 'display_name'
      })
    };
  }

  ngAfterViewInit(): void {
    const interval = setInterval(() => {
      if (typeof window['filterCustomers'] === 'function') {
        clearInterval(interval);
        this.onFiltersChanged();
      }
    }, 100);
  }

  activate ( row : any ) : void {
    this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', { name: row.display_name }))
    .then(() => this.apiService.get('/customers/reactivate_deleted', row.id))
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.CUSTOMER_REACTIVATED'), 'success');
      this.router.stateService.reload();
    });
  }

  getCustomers ( params : any ) : Promise<any> {
    return this.apiService.stream('/filters', [
      this.filtering.value || '*',
      'customers',
      params.offset,
      params.pageSize,
      params.field,
      params.direction,
      this.filtering.archived_only
    ]).then(customers => {
      this.customers = customers;
      return customers;
    });
  }

  onFiltersChanged () : void {
    window['filterCustomers'](this.filtering);
  }

  onRowDoubleClick ( event : any ) : void {
    this.viewCustomer(event.row);
  }

  sendEmail ( row : any ) : void {
    this.messengerService.email({
      attachment_type: 4,
      body           : this.companyMessages?.customer_email_message,
      customer_id    : row.id,
      email_address  : row.email,
      name           : row.company_name
    }).then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success'));
  }

  sendSMS ( row : any ) : void {
    this.messengerService.sms({
      name   : row.display_name,
      send_to: row.mobile
    }).then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success'));
  }

  viewCustomer ( customer : any ) : void {
    if (customer.deleted) {
      return;
    }

    this.router.stateService.go('app.customer', { id: customer.id });
  }
}
